// src/auth/account-lockout-banner.tsx
//
// The account-lockout countdown. Flutter surfaces the lock as a static string
// ("Your Account is locked, please try after N min." —
// login_repository_impl.dart:72, driven by CheckLockOutTime's `minute`). This
// React port keeps that exact copy semantics but renders a LIVE mm:ss countdown
// off the backend's remaining `minute`, and calls `onExpire` when it reaches
// zero so the login button re-enables. Composes the DS `Banner` (warning) atom —
// the message wraps to multiple lines at the auth-card width, which is exactly
// what Banner is for; Alert is a fixed single-line toast and would truncate the
// sentence (and the countdown). Colour comes from the DS, no raw hex.
import { useEffect, useRef, useState } from 'react';
import { Banner } from '@scripsteam/scrips-signal-ds';

interface AccountLockoutBannerProps {
  /** Remaining lockout minutes from CheckLockOutTime (may be fractional). */
  minutes: number;
  /** Fired once the countdown hits zero — the login page re-enables submit. */
  onExpire: () => void;
}

function formatRemaining(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const mm = Math.floor(safe / 60);
  const ss = Math.floor(safe % 60);
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

export function AccountLockoutBanner({ minutes, onExpire }: AccountLockoutBannerProps) {
  const [remaining, setRemaining] = useState(() => Math.ceil(minutes * 60));
  // Keep the latest onExpire without re-subscribing the interval each render.
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Derive the countdown from a fixed wall-clock DEADLINE, not a per-tick
  // decrement. A decrement-once-per-interval clock drifts whenever the tab is
  // backgrounded, the device sleeps, or the browser throttles timers — it would
  // lag real time and keep sign-in disabled after the backend lock has already
  // expired. Computing `remaining` from `Date.now()` every tick (and on
  // visibility change) stays correct across all of those. Re-seeding on
  // `minutes` handles a later failed attempt returning a fresh window.
  useEffect(() => {
    const deadline = Date.now() + minutes * 60_000;
    let fired = false;
    const tick = () => {
      const secs = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 0 && !fired) {
        fired = true;
        window.clearInterval(id);
        onExpireRef.current();
      }
    };
    const id = window.setInterval(tick, 1000);
    // A throttled/suspended background timer may have drifted while hidden —
    // recompute the moment the tab is foregrounded again.
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    tick();
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [minutes]);

  // Flutter copy uses whole minutes ("please try after N min."); the live
  // countdown adds mm:ss precision so the user watches it drain.
  return (
    <Banner variant="warning">
      Your account is locked. Please try again in {formatRemaining(remaining)}.
    </Banner>
  );
}
